import React, { useEffect, useState } from "react";
import { Avatar, Box, Button, Typography, Stack, Grid, Chip } from "@mui/material";
import { CloseOutlined, EditOutlined } from "@mui/icons-material";

import EmptyState from "../EmptyState/EmptyState";
import BoatList from "../BoatList";
import EditBoatModal from "../CollapsedProfile/EditBoatModal";

import ProfileSubtitle from "./ProfileSubtitle";
import { styles } from "./styles";
import EditProfileModal from "./EditProfileModal";
import EditAffiliationsModal from "./EditAffiliationsModal";
import EditInterestsModal from "./EditInterestsModal";

import {
  getAffiliations,
  getUserInformation,
  getUserOptions,
  getInterests,
  getVessels,
  getVesselsSuccess,
  selectVessel,
} from "@/store/slices/user/actions";
import { fromAffiliationsToAffiliationItems } from "@/utils/affiliations";
import { IAffiliationItem } from "@/types/affiliation";
import { Interest } from "@/types/interest";
import useUserInformation from "@/hooks/useUserInformation";
import useUserAffiliations from "@/hooks/useUserAffiliations";
import useUserInterests from "@/hooks/useUserInterests";
import { useAppDispatch, useAppSelector } from "@/hooks/useRedux";
import AddBoatModal from "@/components/CollapsedProfile/AddBoatModal";
import { TVessel } from "@/types/vessel";
import ServiceVessel from "@/services/vessel";

interface ICollapsedProfileProps {
  edit: boolean;
  close: () => void;
  isDrawer?: boolean;
}

const LIFETIME_TAG = "argo_premium_lifetime";

export default function CollapsedProfile({ edit, close, isDrawer }: ICollapsedProfileProps) {
  const dispatch = useAppDispatch();
  const { logged } = useAppSelector((state) => state.auth);
  const { userInterests: interests } = useUserInterests();
  const { userAffiliations: affiliations } = useUserAffiliations();
  const { userInformation } = useUserInformation();
  const [affiliationItems, setAffiliationItems] = useState<IAffiliationItem[]>([]);
  const { vessels } = useAppSelector((state) => state.user);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedVessel, setSelectedVessel] = useState<TVessel | null>(null);
  const [shouldReloadBoats, setShouldReloadBoats] = useState(false);
  const [showAllBoats, setShowAllBoats] = useState(false);

  /**
   * When we receive affiliations we convert them to Items so they can be displayed
   */
  useEffect(() => {
    if (affiliations) {
      setAffiliationItems(fromAffiliationsToAffiliationItems(affiliations));
    }
  }, [affiliations]);

  /**
   * When we detect the login state we dispatch
   * these API calls to retrieve the user stuff
   */
  useEffect(() => {
    if (logged) {
      // Just refresh the user information just in case
      dispatch(getVessels(userInformation?.id));
      dispatch(getAffiliations());
      dispatch(getUserInformation(userInformation?.id));
      dispatch(getUserOptions());
      dispatch(getInterests());
    }
  }, [logged]);

  useEffect(() => {
    if (shouldReloadBoats && userInformation?.id) {
      dispatch(getVessels(userInformation.id));
      setShouldReloadBoats(false);
    }
  }, [shouldReloadBoats, userInformation?.id]);

  // Only to open Edit Modal
  const [openEdit, setOpenEdit] = React.useState(false);

  const handleEditOpen = () => {
    setOpenEdit(true);
  };

  const handleEditClose = () => {
    setOpenEdit(false);
  };

  const [openAddModal, setOpenAddModal] = React.useState(false);

  const handleAddModalOpen = () => {
    setOpenAddModal(true);
  };

  const handleAddModalClose = () => {
    setOpenAddModal(false);
  };

  // Only to open Affiliations Modal
  const [openAffiliationsModal, setOpenAffiliationsModal] = React.useState(false);

  const handleAffiliationsModalOpen = () => {
    setOpenAffiliationsModal(true);
  };

  const handleAffiliationsModalClose = () => {
    setOpenAffiliationsModal(false);
  };

  // Only to open Interests Modal
  const [openInterestsModal, setOpenInterestsModal] = React.useState(false);

  const handleInterestsModalOpen = () => {
    setOpenInterestsModal(true);
  };

  const handleInterestsModalClose = () => {
    setOpenInterestsModal(false);
  };

  const handleEditModalOpen = async (e: React.MouseEvent<HTMLButtonElement>, vessel: TVessel) => {
    e.stopPropagation();
    setSelectedVessel(vessel);
    setOpenEditModal(true);
  };

  const handleEditModalClose = () => {
    setOpenEditModal(false);
    setSelectedVessel(null);
  };

  const hasAffiliations = affiliationItems && affiliationItems.length > 0;
  const hasInterests = interests && interests.length > 0;
  const avatarFallbackSrc = "/img/placeholders/defaultUserMarkerSquare.png";
  const coverFallbackSrc = "/img/placeholders/argoBG.jpg";
  const lifetimePremium = "/img/icons/CheckIcon.svg";

  const url = `url(${userInformation?.cover ? userInformation?.cover : coverFallbackSrc})`;

  const handleChangeBoat = async (id: number) => {
    const vesselToEdit = vessels.find((v) => v.id === id);

    if (vesselToEdit) {
      await ServiceVessel.editVessel(id, { ...vesselToEdit, current: true });
      dispatch(selectVessel(vesselToEdit));
    }
  };

  const getBoatsToShow = () => (showAllBoats ? [...vessels] : vessels.slice(0, 5));

  const handleClose = () => {
    const sortedVessels = [...vessels].sort((a, b) => {
      if (a.current && !b.current) return -1;
      if (!a.current && b.current) return 1;

      return 0;
    });

    dispatch(getVesselsSuccess(sortedVessels));
    close();
  };

  return (
    <Box sx={isDrawer ? styles.mainContainerDrawer : styles.mainContainer}>
      <Box
        className="coverImage"
        sx={{
          backgroundImage: url,
        }}
      >
        {!isDrawer && (
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="flex-end"
            spacing={1.5}
            sx={styles.header}
          >
            <Stack alignItems="center" direction="row" spacing={1.5}>
              {edit && (
                <Button
                  color="whiteBg"
                  size="small"
                  startIcon={<EditOutlined />}
                  variant="outlined"
                  onClick={handleEditOpen}
                >
                  Edit Profile
                </Button>
              )}
              <Button
                aria-label="delete"
                className="closeButton"
                color="whiteBg"
                size="small"
                variant="iconButtonContained"
                onClick={handleClose}
              >
                <CloseOutlined />
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
      <Stack alignItems="flex-end" direction="row" spacing={2} sx={styles.infoContainer}>
        <Avatar
          alt={`${userInformation?.first_name || ""} ${userInformation?.last_name || ""}`}
          src={userInformation?.image ? userInformation?.image : avatarFallbackSrc}
          sx={styles.avatar}
        />
        <Box>
          <Typography sx={styles.userFirstName} variant="h5">
            {`${userInformation?.first_name || ""} ${userInformation?.last_name || ""}`}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Typography color="text.secondary" variant="subtitle2">
              {userInformation?.username || ""}
            </Typography>
            {userInformation?.latest_user_subscription?.membership_tag === LIFETIME_TAG && (
              <Stack alignItems="center" direction="row" spacing={1}>
                <Typography color="text.secondary" variant="subtitle2">
                  •
                </Typography>
                <Avatar alt="Lifetime" src={lifetimePremium} sx={styles.premiumIcon} />
                <Typography color="primary" fontSize={12} variant="subtitle2">
                  Lifetime Member
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>
      </Stack>
      <Stack spacing={3} sx={styles.container}>
        <Box>
          <ProfileSubtitle add action={handleAddModalOpen} title="Boat Information" />

          {vessels && vessels.length > 0 ? (
            <>
              <BoatList
                vessels={getBoatsToShow()}
                onChangeBoat={handleChangeBoat}
                onEditOpenModal={handleEditModalOpen}
              />
              {vessels.length > 5 && !showAllBoats && (
                <Button
                  fullWidth
                  color="primary"
                  sx={{ mt: -1 }}
                  variant="contained"
                  onClick={() => setShowAllBoats(true)}
                >
                  SEE ALL BOATS
                </Button>
              )}
              {vessels.length > 5 && showAllBoats && (
                <Button
                  fullWidth
                  color="primary"
                  sx={{ mt: -1 }}
                  variant="outlined"
                  onClick={() => setShowAllBoats(false)}
                >
                  SEE LESS
                </Button>
              )}
            </>
          ) : (
            <EmptyState
              imageURL="img/placeholders/EmptyStateBoat.svg"
              sx={{ width: "100%", height: "160px", backgroundColor: "grey.50" }}
              title="No Boat Image Yet"
            />
          )}
        </Box>
        <Box>
          <ProfileSubtitle
            divider
            action={handleAffiliationsModalOpen}
            edit={edit}
            title="Affiliations"
          />
          {hasAffiliations && (
            <Grid container columnSpacing={1.25} rowSpacing={1}>
              {affiliationItems.map((item) => (
                <Grid key={item.label} item xs="auto">
                  <Chip icon={item.icon} label={item.label} sx={styles.chipWithIcon} />
                </Grid>
              ))}
            </Grid>
          )}
          {!hasAffiliations && <Typography>No affiliations to show</Typography>}
        </Box>
        <Box>
          <ProfileSubtitle
            divider
            action={handleInterestsModalOpen}
            edit={edit}
            title="Interests"
          />
          {hasInterests && (
            <Grid container columnSpacing={1.5} rowSpacing={1}>
              {interests.map((interest: Interest) => (
                <Grid key={interest.id} item xs="auto">
                  <Chip label={interest.name} />
                </Grid>
              ))}
            </Grid>
          )}
          {!hasInterests && <Typography>No interests to show</Typography>}
        </Box>
      </Stack>
      {openEdit && <EditProfileModal close={handleEditClose} open={openEdit} />}
      {openEditModal && selectedVessel && (
        <EditBoatModal boat={selectedVessel} close={handleEditModalClose} open={openEditModal} />
      )}
      {openAddModal && (
        <AddBoatModal
          close={handleAddModalClose}
          open={openAddModal}
          onSuccess={() => setShouldReloadBoats(true)}
        />
      )}
      {openAffiliationsModal && (
        <EditAffiliationsModal close={handleAffiliationsModalClose} open={openAffiliationsModal} />
      )}
      {openInterestsModal && (
        <EditInterestsModal close={handleInterestsModalClose} open={openInterestsModal} />
      )}
    </Box>
  );
}
